import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Heart, 
  Share2, 
  Truck, 
  RefreshCw, 
  Shield,
  Plus,
  Minus,
  Check,
  Play,
  Globe,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { TechLabel } from '../components/common/TacticalCard';
import { productsApi } from '../lib/api';
import { useCart } from '../contexts/CartContext';
import { useRecentlyViewed } from '../contexts/RecentlyViewedContext';
import { toast } from 'sonner';
import SEO from '../components/common/SEO';

const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { addToRecentlyViewed } = useRecentlyViewed();
  
  const [product, setProduct] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);
  const [specFields, setSpecFields] = useState([]);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        // Fetch product and spec fields in parallel
        const [productRes, specFieldsRes] = await Promise.all([
          productsApi.getBySlug(slug),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/masterdata/spec-fields`)
            .then(r => r.json())
            .catch(() => ({ fields: [] }))
        ]);
        
        const fetchedProduct = productRes.data.product;
        setProduct(fetchedProduct);
        setSimilar(productRes.data.similar);
        setSpecFields(specFieldsRes.fields || []);
        
        // Add to recently viewed
        if (fetchedProduct) {
          addToRecentlyViewed(fetchedProduct);
        }
        
        // Set default selections
        if (fetchedProduct.variants?.length > 0) {
          const firstVariant = fetchedProduct.variants[0];
          setSelectedSize(firstVariant.size);
          setSelectedColor(firstVariant.color);
        }
      } catch (err) {
        console.error('Error fetching product:', err);
        // Use mock data
        setProduct(getMockProduct());
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [slug, addToRecentlyViewed]);

  const getMockProduct = () => ({
    product_id: 'prod_mock123',
    slug: slug,
    name: 'Tactical Pantolon Dayanıklı Rahat HIDDEN13',
    description: 'Premium ripstop kumaştan üretilen, askeri standartlarda dayanıklılık sunan taktik pantolon. 4 yönlü esnek yapısı ile üstün hareket özgürlüğü sağlar. Su itici DWR kaplama ile her koşulda kuru kalın.',
    price: 999,
    original_price: 3329,
    discount_percent: 70,
    images: [
      'https://images.unsplash.com/photo-1594938298603-c8148c4dae35?w=800&q=80',
      'https://images.unsplash.com/photo-1473966968600-fa801b869a1a?w=800&q=80',
      'https://images.unsplash.com/photo-1517445312882-bc9910d016b7?w=800&q=80',
    ],
    brand: 'VAVWEAR',
    category: 'pantolon',
    is_new: true,
    is_featured: true,
    variants: [
      { size: 'S', color: 'Siyah', color_code: '#1a1a1a', stock: 10, sku: 'HIDDEN13-S-BLK' },
      { size: 'M', color: 'Siyah', color_code: '#1a1a1a', stock: 15, sku: 'HIDDEN13-M-BLK' },
      { size: 'L', color: 'Siyah', color_code: '#1a1a1a', stock: 20, sku: 'HIDDEN13-L-BLK' },
      { size: 'XL', color: 'Siyah', color_code: '#1a1a1a', stock: 12, sku: 'HIDDEN13-XL-BLK' },
      { size: 'S', color: 'Haki', color_code: '#6b7c5c', stock: 8, sku: 'HIDDEN13-S-KHK' },
      { size: 'M', color: 'Haki', color_code: '#6b7c5c', stock: 10, sku: 'HIDDEN13-M-KHK' },
      { size: 'L', color: 'Haki', color_code: '#6b7c5c', stock: 14, sku: 'HIDDEN13-L-KHK' },
      { size: 'XL', color: 'Haki', color_code: '#6b7c5c', stock: 6, sku: 'HIDDEN13-XL-KHK' },
    ],
    specs: {
      'Kumaş': 'Ripstop %65 Polyester, %35 Pamuk',
      'Esneklik': '4 Yön Streç',
      'Su İticilik': 'DWR Kaplama',
      'Cep Sayısı': '8 Adet',
      'Bel Tipi': 'Ayarlanabilir',
      'Paça': 'Düz Kesim',
      'Ağırlık': '420g',
      'Menşei': 'Türkiye',
    },
    tags: ['tactical', 'outdoor', 'ripstop', 'su-itici'],
  });

  // Get unique colors and sizes
  const colors = product?.variants 
    ? [...new Map(product.variants.map(v => [v.color, { color: v.color, code: v.color_code }])).values()]
    : [];
  
  const sizes = product?.variants
    ? [...new Set(product.variants.filter(v => v.color === selectedColor).map(v => v.size))]
    : [];

  // Get selected variant
  const selectedVariant = product?.variants?.find(
    v => v.size === selectedSize && v.color === selectedColor
  );

  // Get images to display - variant-specific images take priority
  const displayImages = useMemo(() => {
    if (!product) return [];
    
    // If selected color has variant with images, use those
    if (selectedColor && product.variants) {
      // Find a variant with the selected color that has images
      const colorVariant = product.variants.find(
        v => v.color === selectedColor && v.images && v.images.length > 0
      );
      if (colorVariant && colorVariant.images.length > 0) {
        return colorVariant.images;
      }
    }
    
    // Fallback to product's main images
    return product.images || [];
  }, [product, selectedColor]);

  // Reset selected image when color changes (if images changed)
  useEffect(() => {
    if (displayImages.length > 0 && selectedImage >= displayImages.length) {
      setSelectedImage(0);
    }
  }, [displayImages, selectedImage]);

  // Get the effective price (variant price or product base price)
  const effectivePrice = useMemo(() => {
    if (selectedVariant?.price && selectedVariant.price > 0) {
      return selectedVariant.price;
    }
    return product?.price || 0;
  }, [selectedVariant, product]);

  // Get filtered specs (only show non-empty values)
  const filteredSpecs = useMemo(() => {
    if (!product?.specs) return [];
    
    return Object.entries(product.specs)
      .filter(([key, value]) => value && value.toString().trim() !== '')
      .map(([key, value]) => ({ key, value }));
  }, [product]);

  // Format price
  const formatPrice = (p) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0,
    }).format(p);
  };

  // Handle add to cart
  const handleAddToCart = async () => {
    if (!selectedVariant) {
      toast.error('Lütfen beden ve renk seçin');
      return;
    }

    setAddingToCart(true);
    try {
      await addToCart({
        product_id: product.product_id,
        variant_sku: selectedVariant.sku,
        quantity,
        price: effectivePrice, // Use the effective price (variant or base)
        name: product.name,
        image: displayImages[0] || product.images[0],
        size: selectedSize,
        color: selectedColor,
      });
      toast.success('Ürün sepete eklendi');
    } catch (err) {
      toast.error('Bir hata oluştu');
    } finally {
      setAddingToCart(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-vav-bg pt-20 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-vav-accent-cyan border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-vav-bg pt-20 flex items-center justify-center">
        <div className="text-center">
          <p className="text-vav-fg-muted mb-4">Ürün bulunamadı</p>
          <Link to="/shop" className="tactical-btn">Mağazaya Dön</Link>
        </div>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-vav-bg pt-20" data-testid="product-detail-page">
      {/* SEO Meta Tags */}
      <SEO 
        title={product.name}
        description={product.description || `${product.name} - ${product.brand || 'VAV Wear'} premium taktik ve outdoor giyim. ${product.category} kategorisinde.`}
        image={product.images?.[0]}
        type="product"
        product={{
          name: product.name,
          description: product.description,
          images: product.images,
          brand: product.brand,
          price: effectivePrice,
          currency: product.currency || 'TRY',
          variants: product.variants,
          category: product.category
        }}
      />
      
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center gap-2 text-sm">
          <Link to="/" className="text-vav-fg-muted hover:text-vav-accent-cyan">Ana Sayfa</Link>
          <span className="text-vav-fg-dark">/</span>
          <Link to="/shop" className="text-vav-fg-muted hover:text-vav-accent-cyan">Mağaza</Link>
          <span className="text-vav-fg-dark">/</span>
          <span className="text-vav-fg">{product.name}</span>
        </div>
      </div>

      {/* Product Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Images */}
          <div className="space-y-4">
            {/* Main Image */}
            <div className="relative aspect-[4/5] tactical-card overflow-hidden" data-testid="product-main-image">
              <AnimatePresence mode="wait">
                <motion.img
                  key={displayImages[selectedImage]}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  src={displayImages[selectedImage]}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              </AnimatePresence>
              
              {/* Labels */}
              <div className="absolute top-4 left-4 flex flex-col gap-2">
                {product.is_new && <TechLabel variant="accent">[YENİ]</TechLabel>}
                {product.discount_percent && (
                  <TechLabel variant="warning">%{product.discount_percent} İNDİRİM</TechLabel>
                )}
              </div>

              {/* Actions */}
              <div className="absolute top-4 right-4 flex flex-col gap-2">
                <button 
                  className="w-10 h-10 flex items-center justify-center bg-vav-bg/80 border border-vav-border hover:border-vav-accent-cyan transition-colors"
                  data-testid="wishlist-btn"
                >
                  <Heart className="w-5 h-5 text-vav-fg" />
                </button>
                <button 
                  className="w-10 h-10 flex items-center justify-center bg-vav-bg/80 border border-vav-border hover:border-vav-accent-cyan transition-colors"
                  data-testid="share-btn"
                >
                  <Share2 className="w-5 h-5 text-vav-fg" />
                </button>
              </div>

              {/* Image Navigation Arrows */}
              {displayImages.length > 1 && (
                <>
                  <button
                    onClick={() => setSelectedImage((prev) => (prev === 0 ? displayImages.length - 1 : prev - 1))}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-vav-bg/80 border border-vav-border hover:border-vav-accent-cyan transition-colors"
                    data-testid="prev-image-btn"
                  >
                    <ChevronLeft className="w-5 h-5 text-vav-fg" />
                  </button>
                  <button
                    onClick={() => setSelectedImage((prev) => (prev === displayImages.length - 1 ? 0 : prev + 1))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-vav-bg/80 border border-vav-border hover:border-vav-accent-cyan transition-colors"
                    data-testid="next-image-btn"
                  >
                    <ChevronRight className="w-5 h-5 text-vav-fg" />
                  </button>
                </>
              )}

              <div className="scanline-overlay opacity-20" />
            </div>

            {/* Thumbnails */}
            <div className="flex gap-3 overflow-x-auto pb-2">
              {displayImages.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedImage(idx)}
                  className={`flex-shrink-0 w-20 h-24 border-2 overflow-hidden transition-colors ${
                    selectedImage === idx ? 'border-vav-accent-cyan' : 'border-vav-border hover:border-vav-fg'
                  }`}
                  data-testid={`thumbnail-${idx}`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>

            {/* Color indicator for variant images */}
            {selectedColor && (
              <div className="flex items-center gap-2 text-xs text-vav-fg-muted">
                <span>Seçili renk:</span>
                <span className="text-vav-fg font-medium">{selectedColor}</span>
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            {/* Brand */}
            <div className="flex items-center gap-2">
              <TechLabel variant="gold">{product.brand}</TechLabel>
              {product.is_featured && <TechLabel variant="accent">[FIELD TESTED]</TechLabel>}
            </div>

            {/* Title */}
            <h1 className="font-heading text-3xl sm:text-4xl text-vav-fg" data-testid="product-title">
              {product.name}
            </h1>

            {/* Price */}
            <div className="flex items-baseline gap-4 pb-6 border-b border-vav-border">
              <motion.span 
                key={effectivePrice}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="font-display text-4xl text-vav-fg" 
                data-testid="product-price"
              >
                {formatPrice(effectivePrice)}
              </motion.span>
              {product.original_price && product.original_price > effectivePrice && (
                <span className="text-xl text-vav-fg-dark line-through">
                  {formatPrice(product.original_price)}
                </span>
              )}
              {/* Show variant price indicator */}
              {selectedVariant?.price && selectedVariant.price !== product.price && (
                <span className="text-xs px-2 py-1 bg-vav-gold/20 text-vav-gold rounded">
                  Varyant fiyatı
                </span>
              )}
            </div>

            {/* Description */}
            <p className="text-vav-fg-muted leading-relaxed">
              {product.description}
            </p>

            {/* Color Selection */}
            {colors.length > 0 && (
              <div>
                <p className="text-sm text-vav-fg-muted mb-3">
                  Renk: <span className="text-vav-fg font-medium">{selectedColor}</span>
                </p>
                <div className="flex gap-3 flex-wrap">
                  {colors.map((c) => {
                    // Check if this color has variant-specific images
                    const hasImages = product.variants?.some(
                      v => v.color === c.color && v.images && v.images.length > 0
                    );
                    
                    return (
                      <button
                        key={c.color}
                        onClick={() => {
                          setSelectedColor(c.color);
                          setSelectedImage(0); // Reset to first image when color changes
                          // Reset size if not available in new color
                          const availableSizes = product.variants
                            .filter(v => v.color === c.color)
                            .map(v => v.size);
                          if (!availableSizes.includes(selectedSize)) {
                            setSelectedSize(availableSizes[0]);
                          }
                        }}
                        className={`relative w-10 h-10 rounded-full border-2 transition-all ${
                          selectedColor === c.color 
                            ? 'border-vav-accent-cyan ring-2 ring-vav-accent-cyan/30' 
                            : 'border-vav-border hover:border-vav-fg'
                        }`}
                        style={{ backgroundColor: c.code }}
                        title={c.color}
                        data-testid={`color-${c.color.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {selectedColor === c.color && (
                          <Check className="w-4 h-4 absolute inset-0 m-auto text-white drop-shadow-[0_0_2px_rgba(0,0,0,0.8)]" />
                        )}
                        {/* Badge for colors with variant images */}
                        {hasImages && (
                          <span className="absolute -top-1 -right-1 w-3 h-3 bg-vav-gold rounded-full border border-vav-bg" title="Renk fotoğrafları mevcut" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Size Selection */}
            {sizes.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm text-vav-fg-muted">
                    Beden: <span className="text-vav-fg">{selectedSize}</span>
                  </p>
                  <button className="text-sm text-vav-accent-cyan hover:underline">
                    Beden Rehberi
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {sizes.map((size) => {
                    const variant = product.variants.find(
                      v => v.size === size && v.color === selectedColor
                    );
                    const inStock = variant?.stock > 0;
                    
                    return (
                      <button
                        key={size}
                        onClick={() => inStock && setSelectedSize(size)}
                        disabled={!inStock}
                        className={`w-14 h-10 border text-sm font-medium transition-colors ${
                          selectedSize === size
                            ? 'border-vav-accent-cyan text-vav-accent-cyan bg-vav-accent-cyan/10'
                            : inStock
                            ? 'border-vav-border text-vav-fg hover:border-vav-fg'
                            : 'border-vav-border text-vav-fg-dark line-through cursor-not-allowed'
                        }`}
                        data-testid={`size-${size}`}
                      >
                        {size}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Quantity & Add to Cart */}
            <div className="flex items-center gap-4 pt-4">
              {/* Quantity */}
              <div className="flex items-center border border-vav-border">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-10 h-10 flex items-center justify-center text-vav-fg hover:bg-vav-bg-subtle transition-colors"
                  data-testid="quantity-decrease"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="w-12 text-center text-vav-fg font-medium" data-testid="quantity-value">
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-10 h-10 flex items-center justify-center text-vav-fg hover:bg-vav-bg-subtle transition-colors"
                  data-testid="quantity-increase"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>

              {/* Add to Cart */}
              <button
                onClick={handleAddToCart}
                disabled={addingToCart || !selectedVariant?.stock}
                className="flex-1 tactical-btn disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="add-to-cart-btn"
              >
                {addingToCart ? 'EKLENİYOR...' : 'SEPETE EKLE'}
              </button>
            </div>

            {/* Stock Info */}
            {selectedVariant && (
              <p className={`text-sm ${selectedVariant.stock > 5 ? 'text-vav-accent-green' : 'text-vav-accent-orange'}`}>
                {selectedVariant.stock > 10 
                  ? 'Stokta' 
                  : selectedVariant.stock > 0 
                  ? `Son ${selectedVariant.stock} ürün!`
                  : 'Stokta yok'
                }
              </p>
            )}

            {/* Shipping Info */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-6 border-t border-vav-border">
              <div className="flex items-center gap-3">
                <Truck className="w-5 h-5 text-vav-accent-cyan flex-shrink-0" />
                <div>
                  <p className="text-sm text-vav-fg">Ücretsiz Kargo</p>
                  <p className="text-xs text-vav-fg-muted">1000 TL üzeri</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <RefreshCw className="w-5 h-5 text-vav-accent-cyan flex-shrink-0" />
                <div>
                  <p className="text-sm text-vav-fg">Kolay İade</p>
                  <p className="text-xs text-vav-fg-muted">14 gün içinde</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-vav-accent-cyan flex-shrink-0" />
                <div>
                  <p className="text-sm text-vav-fg">Güvenli Ödeme</p>
                  <p className="text-xs text-vav-fg-muted">256-bit SSL</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Specs & Details */}
        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Technical Specs - Dynamic rendering */}
          {filteredSpecs.length > 0 && (
            <div className="tactical-card p-6" data-testid="technical-specs">
              <h2 className="font-heading text-2xl text-vav-fg mb-6 flex items-center gap-2">
                <TechLabel variant="accent">[TEKNİK]</TechLabel>
                ÖZELLİKLER
              </h2>
              <table className="w-full">
                <tbody>
                  {filteredSpecs.map(({ key, value }, idx) => (
                    <tr key={key} className={idx % 2 === 0 ? 'bg-vav-bg-subtle' : ''}>
                      <td className="py-3 px-4 text-sm text-vav-fg-muted font-mono uppercase w-1/3">{key}</td>
                      <td className="py-3 px-4 text-sm text-vav-fg">{value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Size Guide */}
          <div className={`tactical-card p-6 ${filteredSpecs.length === 0 ? 'lg:col-span-2' : ''}`}>
            <h2 className="font-heading text-2xl text-vav-fg mb-6 flex items-center gap-2">
              <TechLabel variant="default">[BEDEN]</TechLabel>
              TABLOSU
            </h2>
            <table className="w-full text-center">
              <thead>
                <tr className="border-b border-vav-border">
                  <th className="py-3 px-2 text-xs text-vav-fg-muted uppercase">Beden</th>
                  <th className="py-3 px-2 text-xs text-vav-fg-muted uppercase">Bel (cm)</th>
                  <th className="py-3 px-2 text-xs text-vav-fg-muted uppercase">Basen (cm)</th>
                  <th className="py-3 px-2 text-xs text-vav-fg-muted uppercase">Boy (cm)</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-vav-bg-subtle">
                  <td className="py-3 px-2 text-sm text-vav-fg font-medium">S</td>
                  <td className="py-3 px-2 text-sm text-vav-fg-muted">76-81</td>
                  <td className="py-3 px-2 text-sm text-vav-fg-muted">91-96</td>
                  <td className="py-3 px-2 text-sm text-vav-fg-muted">100</td>
                </tr>
                <tr>
                  <td className="py-3 px-2 text-sm text-vav-fg font-medium">M</td>
                  <td className="py-3 px-2 text-sm text-vav-fg-muted">81-86</td>
                  <td className="py-3 px-2 text-sm text-vav-fg-muted">96-101</td>
                  <td className="py-3 px-2 text-sm text-vav-fg-muted">102</td>
                </tr>
                <tr className="bg-vav-bg-subtle">
                  <td className="py-3 px-2 text-sm text-vav-fg font-medium">L</td>
                  <td className="py-3 px-2 text-sm text-vav-fg-muted">86-91</td>
                  <td className="py-3 px-2 text-sm text-vav-fg-muted">101-106</td>
                  <td className="py-3 px-2 text-sm text-vav-fg-muted">104</td>
                </tr>
                <tr>
                  <td className="py-3 px-2 text-sm text-vav-fg font-medium">XL</td>
                  <td className="py-3 px-2 text-sm text-vav-fg-muted">91-96</td>
                  <td className="py-3 px-2 text-sm text-vav-fg-muted">106-111</td>
                  <td className="py-3 px-2 text-sm text-vav-fg-muted">106</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Similar Products */}
        {similar.length > 0 && (
          <div className="mt-16">
            <h2 className="font-heading text-2xl text-vav-fg mb-8">BENZER ÜRÜNLER</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {similar.map((p) => (
                <Link
                  key={p.product_id}
                  to={`/product/${p.slug}`}
                  className="group tactical-card overflow-hidden"
                >
                  <div className="aspect-[3/4] overflow-hidden">
                    <img
                      src={p.images?.[0]}
                      alt={p.name}
                      className="w-full h-full object-cover grayscale-hover"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading text-sm text-vav-fg group-hover:text-vav-accent-cyan transition-colors line-clamp-2">
                      {p.name}
                    </h3>
                    <p className="text-vav-fg font-medium mt-2">{formatPrice(p.price)}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default ProductDetail;
